import React, { useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';

// "What's New" — a reasonable, cheap in-app changelog (Sprint 21.2, BK
// 9/22/26). Deliberately thin: each entry is a short plain-English title +
// summary, not rich content. When a change has a fuller writeup, the entry
// just links out to it (detailsUrl) rather than building a CMS inside COMS.
//
// Read-only by design (BK, same day, after seeing the first pass with an
// admin "+ Add update" form): entries are authored by posting directly to
// POST /release-notes (same one who ships the change writes the entry,
// same discipline as a real commit message) rather than through an in-app
// form — one less UI surface to build and maintain for something that gets
// used maybe once per release.

const MODULE_COLORS = {
  'PB101':        '#a855f7',
  'Retention':    '#3b82f6',
  'Lead Engine':  '#facc15',
  'T2M':          '#eab308',
  'Upload':       '#22c55e',
  'Club Dashboard': '#38bdf8',
};
const CLUB_NAMES = { LEW: 'Lewisville', MCK: 'McKinney', DEN: 'Denton', GB: 'Green Bay', MF: 'Menomonee Falls', WA: 'West Allis' };

function formatDate(d) {
  if (!d) return '';
  const iso = String(d).split('T')[0];
  const [y, m, day] = iso.split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function NoteCard({ note }) {
  const color = MODULE_COLORS[note.module] || '#888';
  return (
    <div style={{
      background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 10,
      padding: '16px 18px', marginBottom: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
        {note.module && (
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
            background: `${color}22`, color, border: `1px solid ${color}44`,
          }}>{note.module}</span>
        )}
        {(note.clubs || []).length > 0 && (
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: 'var(--surface)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>
            {note.clubs.map(c => CLUB_NAMES[c] || c).join(', ')}
          </span>
        )}
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{formatDate(note.published_at)}</span>
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 5 }}>{note.title}</div>
      <div style={{ fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.55, marginBottom: note.details_url ? 8 : 0 }}>
        {note.summary}
      </div>
      {note.details_url && (
        <a href={note.details_url} target="_blank" rel="noopener noreferrer"
          style={{ fontSize: 13, color: 'var(--yellow)', fontWeight: 600, textDecoration: 'none' }}>
          {note.details_url.includes('/training/') ? 'Open the tutorial →' : 'View details →'}
        </a>
      )}
    </div>
  );
}

export default function ChangelogModule() {
  const { get } = useApi();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    get('/release-notes')
      .then(res => setNotes(res?.notes || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="module-page">
      <div className="module-header">
        <span style={{ fontSize: 22 }}>📣</span>
        <h1 className="module-title">What's New</h1>
      </div>

      <div style={{ maxWidth: 680 }}>
        {loading && <div className="empty-state"><div className="spinner" /></div>}

        {!loading && notes.length === 0 && (
          <div className="empty-state"><p>No updates yet.</p></div>
        )}

        {!loading && notes.map(n => <NoteCard key={n.id} note={n} />)}
      </div>
    </div>
  );
}
